import axios, { AxiosError } from "axios";
import { useCallback, useContext } from "react";
import { ethers } from "ethers";
import { AuthContext } from "../../../context/AuthContext";
import { TRADES_API_URL } from "../../../utils/env";
import { FilledTradesEndpointEnum } from "../../../enums/endpoints";
import { ToastEnum, ToastStatusEnum, useToast } from "../../toast";
import { IStakerRewardsMetrics } from "../../../utils/staking";

export interface IStakeRecord {
  Account: string;
  Epoch: number;
  StakeAmount: string;
  StakeTier: string;
  VestingPeriod: number;
  StakingMultiplier: number;
  TradingVolume: number;
  TradingMultiplier: number;
  CombinedMultiplier: number;
  StakeValue: string;
  PercentageShare: number;
  RewardAmount: number;
  _CreatedTimestamp: number;
}

interface IStoreEpochStakeResponse {
  success: boolean;
  message: string;
  data?: {
    recordsInserted: number;
    epoch: number;
  };
}

export const useStoreEpochStake = () => {
  const { tokenId } = useContext(AuthContext);
  const { addToast } = useToast();

  const storeEpochStake = useCallback(
    async (
      epoch: number,
      rewardsMetrics: IStakerRewardsMetrics[]
    ): Promise<boolean> => {
      if (!tokenId) {
        addToast(
          {
            type: ToastEnum.SIMPLE,
            header: "Authentication Required",
            status: ToastStatusEnum.ERROR,
          },
          4000
        );
        return false;
      }

      if (!rewardsMetrics || rewardsMetrics.length === 0) {
        addToast(
          {
            type: ToastEnum.SIMPLE,
            header: "No Data to Store",
            subheader: "Please generate epoch rewards first",
            status: ToastStatusEnum.ERROR,
          },
          4000
        );
        return false;
      }

      try {
        // Transform rewardsMetrics to StakeRecord format
        const currentTimestamp = Date.now();
        const timestampNanoseconds = currentTimestamp * 1e6;

        // Helper function to convert large numbers to string without scientific notation
        // Uses BigInt to avoid precision issues and scientific notation
        const toFixedString = (num: number, stakeAmountStr: string, combinedMultiplier: number): string => {
          // For very large numbers, calculate using BigInt to avoid scientific notation
          // stakeAmount is already a string in wei, so we can use BigInt
          try {
            // Convert stakeAmount to BigInt
            const stakeAmountBigInt = BigInt(stakeAmountStr);
            // Multiply by combinedMultiplier, handling decimals
            // Since combinedMultiplier might have decimals (e.g., 1.7), we multiply by 1000 first
            const multiplierScaled = Math.round(combinedMultiplier * 1000);
            const resultBigInt = (stakeAmountBigInt * BigInt(multiplierScaled)) / BigInt(1000);
            return resultBigInt.toString();
          } catch {
            // Fallback: round and convert, handling scientific notation if it occurs
            const rounded = Math.round(num);
            const str = rounded.toString();
            if (str.includes("e+") || str.includes("E+")) {
              // Parse scientific notation manually
              const match = str.match(/^(-?\d+\.?\d*)e\+(\d+)$/i);
              if (match) {
                const [, base, exp] = match;
                const exponent = parseInt(exp, 10);
                const baseNum = base.replace(".", "");
                const baseLength = baseNum.replace("-", "").length;
                const zerosNeeded = exponent - baseLength + 1;
                return (baseNum.startsWith("-") ? "-" : "") + baseNum.replace("-", "") + "0".repeat(Math.max(0, zerosNeeded));
              }
            }
            return str;
          }
        };

        const stakes: IStakeRecord[] = rewardsMetrics.map((metric) => {
          // Convert account address to checksummed format
          let checksummedAccount = metric.accountAddress;
          try {
            checksummedAccount = ethers.utils.getAddress(metric.accountAddress);
          } catch {
            // If address is invalid, use as-is
          }

          return {
            Account: checksummedAccount,
            Epoch: epoch,
            StakeAmount: metric.stakeAmount, // Already in wei as string
            StakeTier: metric.stakingTier,
            VestingPeriod: metric.vestingPeriod,
            StakingMultiplier: metric.stakingMultiplier,
            TradingVolume: metric.tradeVolume,
            TradingMultiplier: metric.tradingMultiplier,
            CombinedMultiplier: metric.combinedMultiplier,
            StakeValue: toFixedString(metric.stakeValue, metric.stakeAmount, metric.combinedMultiplier), // Convert to string without scientific notation
            PercentageShare: metric.percentageShare,
            RewardAmount: Math.round(metric.rewardAmount), // Round to integer
            _CreatedTimestamp: timestampNanoseconds,
          };
        });

        const response = await axios.post<IStoreEpochStakeResponse>(
          TRADES_API_URL(FilledTradesEndpointEnum.STORE_EPOCH_STAKE),
          { stakes },
          {
            withCredentials: true,
            headers: {
              Authorization: `Bearer ${tokenId}`,
              "Access-Control-Allow-Origin": "*",
            },
          }
        );

        if (response.data.success) {
          addToast(
            {
              type: ToastEnum.SIMPLE,
              header: "Epoch Rewards Stored",
              subheader: `Successfully stored ${response.data.data?.recordsInserted || stakes.length} record(s) for epoch ${epoch}`,
              status: ToastStatusEnum.SUCCESS,
            },
            4000
          );
          return true;
        }
          addToast(
            {
              type: ToastEnum.SIMPLE,
              header: "Failed to Store Epoch Rewards",
              subheader: response.data.message || "Unknown error",
              status: ToastStatusEnum.ERROR,
            },
            4000
          );
          return false;
      } catch (error) {
        const errorMessage =
          (error as AxiosError<{ message?: string }>)?.response?.data?.message ||
          "Failed to store epoch rewards";
        addToast(
          {
            type: ToastEnum.SIMPLE,
            header: "Failed to Store Epoch Rewards",
            subheader: errorMessage,
            status: ToastStatusEnum.ERROR,
          },
          4000
        );
        return false;
      }
    },
    [tokenId, addToast]
  );

  return { storeEpochStake };
};
